import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/supabase/client';
import { useAuth } from '@/lib/auth-context';

interface SiteSettingsRow {
  id?: string;
  value?: unknown;
  hide_broadcast?: boolean;
  hero_title?: string | null;
  hero_subtitle?: string | null;
  hero_cta_label?: string | null;
  hero_cta_url?: string | null;
  hero_image_url?: string | null;
  updated_at?: string | null;
}

interface SiteSettingsForm {
  hide_broadcast: boolean;
  hero_title: string;
  hero_subtitle: string;
  hero_cta_label: string;
  hero_cta_url: string;
  hero_image_url: string;
  updated_at?: string | null;
}

interface TeacherRecord {
  id: string;
  full_name: string | null;
  email: string | null;
}

const DEFAULT_SETTINGS: SiteSettingsForm = {
  hide_broadcast: false,
  hero_title: 'Celebrate the sponsors powering classrooms.',
  hero_subtitle:
    'Discover the companies and community members funding classroom experiences across Project Tower.',
  hero_cta_label: 'Learn more',
  hero_cta_url: '/programs',
  hero_image_url: '',
};

function normalizeSettingsRow(row: SiteSettingsRow | null): SiteSettingsForm {
  if (!row) return { ...DEFAULT_SETTINGS, updated_at: null };
  return {
    hide_broadcast: !!row.hide_broadcast,
    hero_title: row.hero_title ?? DEFAULT_SETTINGS.hero_title,
    hero_subtitle: row.hero_subtitle ?? DEFAULT_SETTINGS.hero_subtitle,
    hero_cta_label: row.hero_cta_label ?? DEFAULT_SETTINGS.hero_cta_label,
    hero_cta_url: row.hero_cta_url ?? DEFAULT_SETTINGS.hero_cta_url,
    hero_image_url: row.hero_image_url ?? '',
    updated_at: row.updated_at ?? null,
  };
}

function isValidCtaUrl(value: string | undefined | null): boolean {
  if (!value) return true;
  const trimmed = value.trim();
  return trimmed.startsWith('/') || trimmed.startsWith('https://');
}

export default function AdminSiteSettings() {
  const { profile, session } = useAuth();
  const isAdmin = profile?.role === 'admin' || profile?.role === 'superadmin';

  const [form, setForm] = useState(() => normalizeSettingsRow(null));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [imageError, setImageError] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testStatus, setTestStatus] = useState('');
  const [testError, setTestError] = useState('');
  const [teachers, setTeachers] = useState<TeacherRecord[]>([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');

  const heroPreviewUrl = useMemo(() => form.hero_image_url?.trim() || '', [form.hero_image_url]);

  useEffect(() => {
    setImageError(false);
  }, [heroPreviewUrl]);

  useEffect(() => {
    if (selectedTeacherId || teachers.length === 0) return;
    setSelectedTeacherId(teachers[0]?.id || '');
  }, [selectedTeacherId, teachers]);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const { data, error: queryError } = await supabase
          .from('site_settings')
          .select('*')
          .eq('id', 'site')
          .maybeSingle();

        if (!isMounted) return;
        if (queryError && queryError.code !== 'PGRST116') {
          throw queryError;
        }
        setForm(normalizeSettingsRow(data));
      } catch (loadError) {
        if (!isMounted) return;
        console.warn('[admin-site-settings] load failed', loadError);
        setError(loadError instanceof Error ? loadError.message : 'Failed to load site settings.');
        setForm(normalizeSettingsRow(null));
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    const loadTeachers = async () => {
      try {
        const { data, error: teacherErr } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .eq('role', 'teacher')
          .order('full_name', { nullsFirst: true })
          .limit(200);

        if (!isMounted) return;
        if (teacherErr) throw teacherErr;
        setTeachers(Array.isArray(data) ? data : []);
      } catch (teacherError) {
        if (!isMounted) return;
        console.warn('[admin-site-settings] teacher load failed', teacherError);
        setTeachers([]);
      }
    };

    load();
    loadTeachers();
    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  const runStorybooksTest = async () => {
    if (testing) return;

    setTesting(true);
    setTestStatus('');
    setTestError('');

    try {
      const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
      const response = await fetch(`${CORE_API}/api/v1/admin/storybooks-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ teacher_id: selectedTeacherId || null }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || 'Failed to run storybooks test');
      }

      setTestStatus(payload?.message || 'Storybooks pipeline test completed.');
    } catch (runError) {
      console.error('[admin-site-settings] storybooks test failed', runError);
      setTestError(runError instanceof Error ? runError.message : 'Failed to run storybooks pipeline test.');
    } finally {
      setTesting(false);
    }
  };

  if (!isAdmin) {
    return <div className="p-6">You do not have permission to view this page.</div>;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;

    const trimmedHeroUrl = form.hero_cta_url?.trim() || '';
    if (!isValidCtaUrl(trimmedHeroUrl)) {
      setError('Hero CTA URL must start with "/" or "https://".');
      setStatus('');
      return;
    }

    setSaving(true);
    setError('');
    setStatus('');

    const payload = {
      id: 'site',
      hide_broadcast: !!form.hide_broadcast,
      hero_title: form.hero_title?.trim() || DEFAULT_SETTINGS.hero_title,
      hero_subtitle: form.hero_subtitle?.trim() || DEFAULT_SETTINGS.hero_subtitle,
      hero_cta_label: form.hero_cta_label?.trim() || DEFAULT_SETTINGS.hero_cta_label,
      hero_cta_url: trimmedHeroUrl || DEFAULT_SETTINGS.hero_cta_url,
      hero_image_url: form.hero_image_url?.trim() || null,
      updated_at: new Date().toISOString(),
    };

    try {
      const { error: upsertError } = await supabase.from('site_settings').upsert(payload, { onConflict: 'id' });
      if (upsertError) throw upsertError;
      setStatus('Settings saved.');
      setForm(normalizeSettingsRow(payload));
    } catch (saveError) {
      console.error('[admin-site-settings] save failed', saveError);
      setError(saveError instanceof Error ? saveError.message : 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-cocoa">Site Settings</h1>
        <p className="text-sm text-cocoa/70">Control how the public homepage is presented.</p>
      </header>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}
      {status ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{status}</div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-6">
        <fieldset className="space-y-4 rounded-2xl border border-white/70 bg-white p-4 shadow-sm">
          <legend className="px-2 text-sm font-semibold uppercase tracking-wide text-cocoa/60">Broadcast</legend>
          <label className="flex items-start gap-3 text-sm text-cocoa">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={!!form.hide_broadcast}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  hide_broadcast: event.target.checked,
                }))
              }
              disabled={loading || saving}
            />
            <span className="leading-5">
              Hide broadcast section on the homepage. Visitors will not see the live broadcast module when enabled.
            </span>
          </label>
        </fieldset>

        <fieldset className="space-y-4 rounded-2xl border border-white/70 bg-white p-4 shadow-sm">
          <legend className="px-2 text-sm font-semibold uppercase tracking-wide text-cocoa/60">Hero</legend>
          <div className="space-y-1">
            <label className="text-sm font-medium text-cocoa" htmlFor="hero-title">
              Title
            </label>
            <input
              id="hero-title"
              type="text"
              value={form.hero_title}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  hero_title: event.target.value,
                }))
              }
              className="w-full rounded-lg border border-white/70 bg-shell/30 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
              disabled={loading || saving}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-cocoa" htmlFor="hero-subtitle">
              Subtitle
            </label>
            <textarea
              id="hero-subtitle"
              value={form.hero_subtitle}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  hero_subtitle: event.target.value,
                }))
              }
              rows={3}
              className="w-full rounded-lg border border-white/70 bg-shell/30 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
              disabled={loading || saving}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm font-medium text-cocoa" htmlFor="hero-cta-label">
                CTA label
              </label>
              <input
                id="hero-cta-label"
                type="text"
                value={form.hero_cta_label}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    hero_cta_label: event.target.value,
                  }))
                }
                className="w-full rounded-lg border border-white/70 bg-shell/30 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
                disabled={loading || saving}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-cocoa" htmlFor="hero-cta-url">
                CTA URL
              </label>
              <input
                id="hero-cta-url"
                type="text"
                value={form.hero_cta_url}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    hero_cta_url: event.target.value,
                  }))
                }
                className="w-full rounded-lg border border-white/70 bg-shell/30 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
                placeholder="/programs or https://example.com"
                disabled={loading || saving}
              />
              <p className="text-xs text-cocoa/60">Must start with "/" or "https://".</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-cocoa" htmlFor="hero-image-url">
                Hero image URL (optional)
              </label>
              {heroPreviewUrl ? (
                <a
                  href={heroPreviewUrl}
                  className="text-xs font-medium text-terracotta hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open image
                </a>
              ) : null}
            </div>
            <input
              id="hero-image-url"
              type="url"
              value={form.hero_image_url}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  hero_image_url: event.target.value,
                }))
              }
              placeholder="https://example.com/hero.jpg"
              className="w-full rounded-lg border border-white/70 bg-shell/30 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
              disabled={loading || saving}
            />
            {heroPreviewUrl ? (
              <div className="overflow-hidden rounded-xl border border-white/70 bg-shell/20">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={heroPreviewUrl}
                  alt="Hero preview"
                  className="h-40 w-full object-cover"
                  onError={() => setImageError(true)}
                />
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-white/70 bg-shell/10 p-4 text-sm text-cocoa/60">
                No custom image. The default illustration will be used.
              </div>
            )}
            {imageError ? (
              <p className="text-xs text-red-600">Unable to load preview. The homepage will fall back to the default art.</p>
            ) : null}
          </div>
        </fieldset>

        <fieldset className="space-y-3 rounded-2xl border border-white/70 bg-white p-4 shadow-sm">
          <legend className="px-2 text-sm font-semibold uppercase tracking-wide text-cocoa/60">Storybooks</legend>
          <p className="text-sm text-cocoa/70">
            Run a full storybooks pipeline smoke test (participants, illustrations, and PDF) with one click.
          </p>

          <div className="space-y-1">
            <label className="text-sm font-medium text-cocoa" htmlFor="storybook-teacher">
              Test as teacher
            </label>
            <select
              id="storybook-teacher"
              value={selectedTeacherId}
              onChange={(event) => setSelectedTeacherId(event.target.value)}
              className="w-full rounded-lg border border-white/70 bg-shell/30 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
              disabled={loading || saving || testing || teachers.length === 0}
            >
              <option value="">Select a teacher (required)</option>
              {teachers.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.full_name || teacher.email || teacher.id}
                </option>
              ))}
            </select>
            <p className="text-xs text-cocoa/60">Choose a live teacher account to exercise the teacher-only PDF endpoint.</p>
          </div>

          {testError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{testError}</div>
          ) : null}
          {testStatus ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {testStatus}
            </div>
          ) : null}

          <button
            type="button"
            className="inline-flex items-center justify-center rounded-lg bg-cocoa px-4 py-2 text-sm font-semibold text-white shadow disabled:opacity-60"
            onClick={runStorybooksTest}
            disabled={loading || saving || testing || !selectedTeacherId}
          >
            {testing ? 'Testing storybooks…' : 'Test storybooks pipeline'}
          </button>
        </fieldset>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            className="rounded-lg bg-terracotta px-4 py-2 text-sm font-semibold text-white shadow disabled:opacity-60"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save settings'}
          </button>
          {form.updated_at ? (
            <span className="text-xs uppercase tracking-wide text-cocoa/60">
              Last updated {new Date(form.updated_at).toLocaleString()}
            </span>
          ) : null}
        </div>
      </form>
    </div>
  );
}
